<template>
  <Teleport :to="to || 'body'">
    <el-dialog
      v-model="isOpen"
      class="empty-modal-dialog md:p-8 md:max-w-[1200px] w-[99%] mx-auto md:w-fit !bg-bgnew"
      :class="classes"
      :width="modalWidth"
      :lock-scroll="false"
      destroy-on-close
    >
      <slot />
    </el-dialog>
  </Teleport>
</template>

<script setup lang="ts">
  import { useScreenSize } from "~/helpers/isMobile";
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const props = defineProps<{
    open?: boolean;
    classes?: string;
    width?: string;
    responsiveWidth?: string[];
    to?: HTMLElement;
  }>();
  const emit = defineEmits(["update:open"]);
  const isOpen = ref<boolean>(false);

  // Переменная для сохранения позиции скролла
  let savedScrollPosition = 0;

  const modalWidth = computed(() => {
    if (isMobile.value) return `${props?.responsiveWidth?.[2] || "99%"}`;
    if (isTablet.value) return `${props?.responsiveWidth?.[1] || "720px"}`;
    if (isDesktop.value) return `${props?.width || props?.responsiveWidth?.[0] || "810px"}`;
    return "50%";
  });

  // Функция для предотвращения скролла фона и сохранения позиции
  const preventBodyScroll = (shouldPrevent: boolean) => {
    if (shouldPrevent) {
      // Сохраняем текущую позицию скролла
      savedScrollPosition = window.scrollY;

      // Блокируем скролл фона. overflow: hidden не ставим: body высотой в экран,
      // а страница длиннее — он обрезал бы и её, и выпадающие списки, которые
      // рисуются внутри body. Документ не скроллится и так: body зафиксирован
      document.body.style.position = "fixed";
      document.body.style.top = `-${savedScrollPosition}px`;
      document.body.style.width = "100%";
    } else {
      // Восстанавливаем скролл
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      // Восстанавливаем позицию скролла. instant: у html scroll-behavior: smooth,
      // и страница иначе заметно «проезжала» бы от верха до прежнего места
      window.scrollTo({ top: savedScrollPosition, behavior: "instant" });
    }
  };

  // Следим за изменениями props.open
  watch(
    () => props.open,
    (newVal) => {
      isOpen.value = newVal;
    },
    { immediate: true }
  );

  // Следим за изменениями isOpen и эмитируем событие
  watch(isOpen, (newVal) => {
    emit("update:open", newVal);

    // Управляем скроллом при открытии/закрытии модального окна
    if (process.client) {
      preventBodyScroll(newVal);
    }
  });

  // Очищаем стили при размонтировании компонента
  onUnmounted(() => {
    if (process.client) {
      preventBodyScroll(false);
    }
  });
</script>

<style lang="scss">
  .empty-modal-dialog {
    border-radius: 32px;

    .el-dialog__body {
      padding: 0;
    }

    .el-dialog__headerbtn {
      top: 0;
      right: -42px;
      background-color: theme("colors.bgnew");
      padding: 4px;
      height: auto;
      width: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 30px;

      @media (max-width: 768px) {
        right: 0;
        top: -42px;
      }
    }

    .el-dialog__header {
      padding: 0;
    }

    .el-dialog__close {
      height: 24px;
      width: 24px;

      svg {
        height: 24px;
        width: 24px;
      }
    }
  }
</style>
